import React, {useEffect, useState} from "react";
import {Button, Form, Header, Input, Message, Modal, Segment} from "semantic-ui-react";
import {toast} from "react-toastify";
import {ToasterConfig} from "../../../config/ToasterConfig";
import useReferenceGroupService from "../../../services/backoffice/hooks/useReferenceGroupService";
import {UpdateReferenceGroupRequest} from "../../../models/requests/update-reference-group-request";

interface ReferenceGroupFormProps {
    isModal?: boolean;
    open?: boolean;
    onClose?: () => void;
    referenceGroupId?: string;
}

const ReferenceGroupForm: React.FC<ReferenceGroupFormProps> = ({
                                                                   isModal = false,
                                                                   open = false,
                                                                   onClose,
                                                                   referenceGroupId
                                                               }) => {
    const [group, setGroup] = useState<Partial<UpdateReferenceGroupRequest>>({});
    const [loading, setLoading] = useState<boolean>(false);
    const [error, setError] = useState<string | null>(null);
    const {createReferenceGroup, updateReferenceGroup, getReferenceGroupById} = useReferenceGroupService();

    useEffect(() => {
        if (referenceGroupId) {
            setLoading(true);
            getReferenceGroupById(referenceGroupId)
                .then((response) => {
                    setGroup(response);
                })
                .catch(() => {
                    setError("Erro ao carregar grupo de referência.");
                })
                .finally(() => {
                    setLoading(false);
                });
        }
    }, [referenceGroupId]);

    const handleSubmit = async () => {
        try {
            if (!group.code || !group.name) {
                setError("Todos os campos são obrigatórios.");
                return;
            }

            setLoading(true);
            if (referenceGroupId) {
                await updateReferenceGroup(referenceGroupId, {name: group.name, code: group.code});
                toast.success("Grupo de referência atualizado com sucesso", ToasterConfig.success);
            } else {
                await createReferenceGroup({name: group.name, code: group.code});
                toast.success("Grupo de referência criado com sucesso", ToasterConfig.success);
            }
            if (onClose) onClose();
        } catch {
            setError("Erro ao salvar grupo de referência.");
        } finally {
            setLoading(false);
        }
    };

    const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const {name, value} = event.target;
        setGroup((prevGroup) => ({
            ...prevGroup,
            [name]: value,
        }));
    };

    const handleClear = () => {
        setGroup({});
    };

    const isSubmitDisabled = !group.code || !group.name;

    return  (
        <Segment raised style={{maxWidth: "600px", margin: "5px auto", padding: "20px"}}>
            <Header
                as="h3"
                style={{
                    background: "#49AB3AFF",
                    color: "#fff",
                    display: "flex",
                    alignItems: "left",
                    justifyContent: "flex-start",
                    padding: "10px",
                    margin: "0",
                    borderRadius: "5px 5px 0 0",
                }}
            >
                {referenceGroupId ? "Editar Grupo de Referência" : "Cadastro de Grupos de Referência"}
            </Header>

            <Form onSubmit={handleSubmit} style={{marginTop: "15px"}}>
                <div
                    style={{
                        border: "2px solid #d4d4d5",
                        borderRadius: "4px",
                        padding: "10px",
                        margin: "10px 0",
                    }}
                >
                    <Form.Group widths="equal">
                        <Form.Field required>
                            <label>Código</label>
                            <Input
                                name="code"
                                placeholder="Digite o código"
                                onChange={handleInputChange}
                                value={group.code || ""}
                                disabled={loading}
                                style={{padding: "10px", width: "100%"}}
                                fluid
                                error={isSubmitDisabled && !group.code}
                            />
                        </Form.Field>

                        <Form.Field required>
                            <label>Nome do Grupo de Referência</label>
                            <Input
                                name="name"
                                placeholder="Digite o nome"
                                onChange={handleInputChange}
                                value={group.name || ""}
                                disabled={loading}
                                style={{padding: "10px", width: "100%"}}
                                fluid
                                error={isSubmitDisabled && !group.name}
                            />
                        </Form.Field>
                    </Form.Group>
                </div>

                <div style={{textAlign: "right"}}>
                    <Button color="green" type="submit" disabled={isSubmitDisabled || loading}>
                        {referenceGroupId ? "Atualizar" : "Salvar"}
                    </Button>
                    <Button type="button" onClick={handleClear} disabled={loading}>
                        Limpar
                    </Button>
                    <Button type="button" onClick={onClose} disabled={loading}>
                        Cancelar
                    </Button>
                </div>

                {error && <Message negative>{error}</Message>}
            </Form>
        </Segment>
    );
};

export default ReferenceGroupForm;
